import { tenantSelector } from "@/react/features/customer/customerSlice";
import { useSelector } from "react-redux";
import {
  useGetPhoneChannelsQuery,
  handleUpdateResponse,
  useUpdatePhoneChannelsMutation,
} from "./phoneChannelsApi";
import { useTable } from "@/react/components/table/useTable";
import schema from "./schema";
import columnDefinition from "./columnDefinition";
import TableComponent from "@/react/components/table/TableComponent";
import React, { useCallback, useMemo } from "react";
import { errorNotification, useErrorNotification } from "@/react/notifications";
import { useGetProfileQuery } from "../profile/profileApi";
import { omit } from "lodash";
import TableWrapper from "@/react/features/styled/TableWrapper";
import PageContentContainer from "@/react/features/styled/PageContentContainer";
import PageTitle from "@/react/components/PageTitle";

export const PhoneChannelsPage = () => {
  const tenant = useSelector(tenantSelector);
  const tenantName = tenant.tenantId;
  const { data: profile } = useGetProfileQuery(tenantName);

  const phoneChannelsColumns = useMemo(() => {
    return columnDefinition();
  }, [profile]);

  const {
    data: initialData,
    isFetching: isLoading,
    error,
    refetch,
  } = useGetPhoneChannelsQuery(tenantName);

  const [updatePhoneChannels, { isLoading: isUpdating }] =
    useUpdatePhoneChannelsMutation();

  useErrorNotification(error);

  const { table, filters, state, mutations } = useTable(
    initialData,
    phoneChannelsColumns,
    schema,
    schema.fields.id.spec.key
  );

  const {
    rowsToUpdate,
    resetData,

    setData,
    setRowsToUpdate,
  } = mutations;

  const isDirty = rowsToUpdate.size > 0;

  const onSave = useCallback(() => {
    if (rowsToUpdate.size > 0) {
      updatePhoneChannels({
        tenantName,
        phoneChannels: Array.from(rowsToUpdate.values()),
      })
        .unwrap()
        .then(handleUpdateResponse({ rowsToUpdate, setRowsToUpdate, setData }))
        .catch(errorNotification);
    }
  }, [rowsToUpdate]);

  const isTableLoading = isLoading || isUpdating;

  // Remove add and delete operations from the final set of possible mutations,
  // since those are not allowed by this API.
  const finalMutations = React.useMemo(
    () => omit(mutations, ["addRows", "deleteRows"]),
    [mutations]
  );

  return (
    <PageContentContainer>
      <PageTitle>Phone Channels</PageTitle>
      <TableWrapper>
        <TableComponent
          title="Phone Channels"
          schema={schema}
          isDirty={isDirty}
          isLoading={isTableLoading}
          onSave={onSave}
          onCancel={resetData}
          onRefresh={refetch}
          table={table}
          filters={filters}
          mutations={finalMutations}
          state={state}
        />
      </TableWrapper>
    </PageContentContainer>
  );
};
